'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { navigation, siteConfig, footerSections } from '@/lib/content'
import { navigationFr, siteConfigFr, footerSectionsFr } from '@/lib/content-fr'
import { navigationEs, siteConfigEs, footerSectionsEs } from '@/lib/content-es'
import { Logo } from '@/components/logo'
import { getCurrentLocale } from '@/lib/utils'
import { processMarkdownFr } from '@/lib/content-fr'

export function Footer() {
  const pathname = usePathname()
  const currentLocale = getCurrentLocale(pathname)

  // Get locale-specific content
  const getNavigation = () => {
    switch (currentLocale) {
      case 'es':
        return navigationEs
      case 'fr':
        return navigationFr
      case 'en':
      default:
        return navigation
    }
  }

  const getSiteConfig = () => {
    switch (currentLocale) {
      case 'es':
        return siteConfigEs
      case 'fr':
        return siteConfigFr
      case 'en':
      default:
        return siteConfig
    }
  }

  const getFooterSections = () => {
    switch (currentLocale) {
      case 'es':
        return footerSectionsEs
      case 'fr':
        return footerSectionsFr
      case 'en':
      default:
        return footerSections
    }
  }

  const nav = getNavigation()
  const config = getSiteConfig()
  const sections = getFooterSections()

  // Process description for HTML rendering
  const getProcessedDescription = () => {
    if (currentLocale === 'fr') {
      return processMarkdownFr(config.description)
    }
    return config.description
  }

  return (
    <footer className="border-t bg-muted/50">
      <div className="container py-8 md:py-12">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-4">
          <div className="space-y-3">
            <Logo />
            <p className="text-sm text-muted-foreground max-w-xs"
               dangerouslySetInnerHTML={{ __html: getProcessedDescription() }}>
            </p>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold">{sections.product}</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link href="/features" className="text-muted-foreground hover:text-foreground">
                  {nav?.main?.find(item => item.href === '/features')?.name || 'Features'}
                </Link>
              </li>
              <li>
                <Link href="/pricing" className="text-muted-foreground hover:text-foreground">
                  {nav?.main?.find(item => item.href === '/pricing')?.name || 'Pricing'}
                </Link>
              </li>
              <li>
                <Link href="/use-cases" className="text-muted-foreground hover:text-foreground">
                  {currentLocale === 'fr' ? 'Secteurs' : currentLocale === 'es' ? 'Sectores' : 'Use Cases'}
                </Link>
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold">{sections.company}</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link href="/faqs" className="text-muted-foreground hover:text-foreground">
                  {nav?.main?.find(item => item.href === '/faqs')?.name || 'FAQs'}
                </Link>
              </li>
              <li>
                <Link href="mailto:contact@smartq.prod" className="text-muted-foreground hover:text-foreground">
                  {nav?.footer?.find(item => item.href === 'mailto:contact@smartq.prod')?.name || 'Contact'}
                </Link>
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold">{sections.legal}</h4>
            <ul className="space-y-2 text-sm">
              {nav.footer.map((item) => (
                <li key={item.href}>
                  <Link href={item.href} className="text-muted-foreground hover:text-foreground">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-8 border-t pt-8 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} {config.name}. All rights reserved.</p>
        </div>
      </div>
    </footer>
  )
}
